import {useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {RadioGroup as MuiRadioGroup} from '@mui/material'
import {StyledRadio, StyledFormControlLabel, RadioContainer} from './RadioGroupStyles'

/**
 * RadioGroup - Independent radio button group component
 *
 * A standalone, reusable radio group component.
 * Can be used anywhere in your application without FilterHeader dependency.
 *
 * @param {Object} props
 * @param {Array} props.options - Array of option objects with {label, value} structure
 * @param {string|number} props.value - Currently selected value
 * @param {Function} props.onChange - Callback when selection changes (event, value)
 * @param {string} props.orientation - Layout orientation: 'horizontal' or 'vertical' (default: 'vertical')
 * @param {string} props.ariaLabel - Aria label for accessibility
 * @param {boolean} props.disabled - Whether the entire group is disabled
 * @param {string} props.name - Name attribute for the radio group
 */
const RadioGroup = ({
  options = [],
  value = null,
  onChange,
  orientation = 'vertical',
  ariaLabel = 'Radio group',
  disabled = false,
  name,
  ...rest
}) => {
  const [selectedValue, setSelectedValue] = useState(value)

  // Update local state when external value changes
  useEffect(() => {
    setSelectedValue(value)
  }, [value])

  const handleChange = (event) => {
    const newValue = event.target.value
    setSelectedValue(newValue)

    if (onChange) {
      onChange(event, newValue)
    }
  }

  return (
    <RadioContainer orientation={orientation} {...rest}>
      <MuiRadioGroup
        aria-label={ariaLabel}
        name={name}
        value={selectedValue !== null && selectedValue !== undefined ? selectedValue : ''}
        onChange={handleChange}
        sx={{
          display: 'flex',
          pl: 0,
          flexDirection: orientation === 'horizontal' ? 'row' : 'column',
          gap: orientation === 'horizontal' ? 1 : 0.5
        }}
      >
        {options.map((option) => (
          <StyledFormControlLabel
            key={option.value}
            value={option.value}
            control={<StyledRadio />}
            label={option.label}
            disabled={disabled || option.disabled}
          />
        ))}
      </MuiRadioGroup>
    </RadioContainer>
  )
}

RadioGroup.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      disabled: PropTypes.bool
    })
  ).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  orientation: PropTypes.oneOf(['horizontal', 'vertical']),
  ariaLabel: PropTypes.string,
  disabled: PropTypes.bool,
  name: PropTypes.string
}

export default RadioGroup
